import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showUserPhone } from '@/pages/users/api';
import { formatUnixDate } from '@/shared/lib/datetime';
import { formatPhone } from '@/shared/lib/formatPhone';
import { useListFilters } from '@/shared/lib/useListFilters';
import { DataTable, type DataColumn } from '@/shared/ui/DataTable';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import {
  EMPTY_METRIKA_FILTERS,
  fetchMetrikaFilters,
  fetchMetrikaUsers,
  type MetrikaUser,
} from './api';
import { MetrikaFilterBar, type MetrikaFilterName } from './MetrikaFilterBar';
import { MetrikaUsersExport } from './MetrikaUsersExport';

const FILTER_FIELDS: MetrikaFilterName[] = [
  'search',
  'dateStart',
  'dateEnd',
  'period',
  'device',
  'roles',
  'source',
  'partners',
  'os',
  'isApp',
];

export const MetrikaUsersTab = () => {
  const { draft, applied, page, setPage, setField, submit, reset } = useListFilters(EMPTY_METRIKA_FILTERS);
  const [phones, setPhones] = useState<Record<string, string>>({});

  const options = useQuery({ queryKey: ['metrika-filters'], queryFn: fetchMetrikaFilters });
  const users = useQuery({
    queryKey: ['metrika-users', applied, page],
    queryFn: () => fetchMetrikaUsers(applied, page),
  });

  const phoneMutation = useMutation({
    mutationFn: (userId: number) => showUserPhone(userId),
    onSuccess: (res, userId) => {
      if (res.success === 1 && res.phone) setPhones((prev) => ({ ...prev, [userId]: res.phone! }));
    },
  });

  const rows = users.data?.users ?? [];
  const hasGoals = rows.some((user) => user.goals?.length);

  const renderPhone = ({ user_id, phone }: MetrikaUser) => {
    const visible = phones[user_id] ?? phone;
    if (visible) return formatPhone(visible);
    if (phone === null) return null;
    return (
      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={phoneMutation.isPending}
        onClick={() => phoneMutation.mutate(user_id)}
      >
        Показать
      </button>
    );
  };

  const userLink = (user: MetrikaUser, text: string | number) => (
    <Link to={`/users/user/${user.user_id}`}>{text}</Link>
  );

  const columns: DataColumn<MetrikaUser>[] = [
    { header: '№', render: (user) => user.id },
    { header: 'ID', render: (user) => userLink(user, user.user_id) },
    { header: 'Имя', render: (user) => userLink(user, user.user_name) },
    { header: 'Телефон', render: renderPhone },
    { header: 'E-mail', render: (user) => user.email },
    { header: 'Роль', render: (user) => user.role },
    { header: 'Источник', render: (user) => user.source },
    { header: 'Устройство', render: (user) => user.device },
    { header: 'Операционка', render: (user) => user.os },
    { header: 'Приложение', render: (user) => user.app },
    { header: 'Ремонтов', render: (user) => user.bids },
    { header: 'Дата посещения', render: (user) => formatUnixDate(user.visit_date) },
    ...(hasGoals
      ? [
          {
            header: 'Цели',
            render: (user: MetrikaUser) =>
              user.goals?.map((goal) => <div key={goal.id}>{`Цель ${goal.id}: ${goal.name}`}</div>),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="box">
        <div className="box-body">
          <MetrikaUsersExport filters={draft} />
          <MetrikaFilterBar
            fields={FILTER_FIELDS}
            options={options.data}
            values={draft}
            onChange={setField}
            onSubmit={submit}
            onReset={reset}
          />
        </div>
      </div>
      <div className="box">
        <div className="box-body">
          <QueryStatus isLoading={users.isLoading} error={users.error} errorText="Ошибка загрузки пользователей" />
          <DataTable columns={columns} rows={rows} rowKey={(user) => user.id} />
        </div>
        <Pagination page={page} totalPages={users.data?.totalPages ?? 1} onChange={setPage} />
      </div>
    </>
  );
};
